import React, { useCallback } from 'react';
import { components } from 'react-select'
import i18next from 'i18next';

import { TextInput } from "../TextInput";
import { SelectInput, SelectIconRenderer } from '../SelectInput';
import { CheckboxInput } from '../CheckboxInput';
import { connect } from 'react-redux';
import { UserUnitService } from '../../services';

const constraintTypeOptions = [    
    { label: "<None>", value: null },
    { label: "Under", value: "UNDER" },
    { label: "Over", value: "OVER" },
    { label: "Between", value: "BETWEEN" },
];

const SelectIconSingleValue = ({ children, ...props }) => (
    <components.SingleValue {...props}>
        {SelectIconRenderer(props.selectProps.value)}
    </components.SingleValue>
);

const SelectIconOption = (props) => (
    <components.Option {...props}>
        {SelectIconRenderer(props.data)}
    </components.Option>
);

const TemperatureStep = ({step, readOnly, checklistTagOptions, onCorrectiveAction, onStepUpdate, me}) => {
    const updateConstraintType = useCallback((constraintTypeOption) => {
        onStepUpdate({ ...step, constraintType: constraintTypeOption.value, constraintLowValue: null, constraintHighValue: null });
    }, [step, onStepUpdate]);

    const updateConstraintLowValue = useCallback((value) => {
        const pValue = parseFloat(value);
        onStepUpdate({
            ...step, 
            constraintLowValueDisplay: value,
            constraintLowValue: isNaN(pValue) ? value : UserUnitService.convertFromUserUnitToSystemUnit(me.temperatureDisplayUnit, pValue)
        });
    }, [step, onStepUpdate]);

    const updateConstraintHighValue = useCallback((value) => {
        const pValue = parseFloat(value);
        onStepUpdate({
            ...step,
            constraintHighValueDisplay: value,
            constraintHighValue: isNaN(pValue) ? value : UserUnitService.convertFromUserUnitToSystemUnit(me.temperatureDisplayUnit, pValue),
        });
    }, [step, onStepUpdate]);

    const removeConstraintCorrectiveAction = useCallback(() => {
        onStepUpdate({ ...step, constraintCorrectiveAction: null });
    }, [step, onStepUpdate]);

    const updateTag = useCallback((tagOption) => {
        onStepUpdate({ ...step, checklistTag: tagOption.tag });
    }, [step, onStepUpdate]);

    const setCorrectiveAction = useCallback(() => {
        let correctiveAction = step.constraintCorrectiveAction;
        if (correctiveAction == null) {
            correctiveAction = {
                name: `${step.name} - Constraint Corrective Action`,
                correctiveAction: true,
                checklistTaskOptionConfigs: [],
            };
            onStepUpdate({ ...step, constraintCorrectiveAction: correctiveAction });
        }

        let stepsSorted = [...correctiveAction.checklistTaskOptionConfigs];
        stepsSorted.sort((a, b) => { return a.optionOrder - b.optionOrder; });

        var stepIndex = 0;
        onCorrectiveAction({
            correctiveAction: {
                ...correctiveAction,
                checklistTaskOptionConfigs: stepsSorted.map((step) => ({ ...step, uid: stepIndex++ })),
            },
            ownerType: "temperature",
            ownerName: step.name,
            ownerStepUpdateFunction: (updatedCorrectiveAction) => ({ ...step, constraintCorrectiveAction: updatedCorrectiveAction }),
        });
    }, [step, onStepUpdate, onCorrectiveAction]);

    const toggleRecordHistory = useCallback(() => {
        if (!readOnly) {
            onStepUpdate({ ...step, recordHistory: !step.recordHistory });
        }
    }, [step, onStepUpdate]);

    const togglePassiveOnly = useCallback(() => {
        if (!readOnly) {
            onStepUpdate({ ...step, passiveOnly: !step.passiveOnly });
        }
    }, [step, onStepUpdate]);

    const togglePassiveContinuousReading = useCallback(() => {
        if (!readOnly) {
            onStepUpdate({ ...step, passiveContinuousReading: !step.passiveContinuousReading });
        }
    }, [step, onStepUpdate]);

    const togglePassiveRequireUserSignoff = useCallback(() => {
        if (!readOnly) {
            onStepUpdate({ ...step, passiveRequireUserSignoff: !step.passiveRequireUserSignoff });
        }
    }, [step, onStepUpdate]);

    return step.checklistTaskOptionType.token === 'temperature' && (
        <div>
            <div className="form-group row">
                <div className='col-sm-6'>
                    <label>Passive Device Tag (optional)</label>
                    <SelectInput
                        input={{
                            value: step.checklistTag ? { label: step.checklistTag.name, tag: step.checklistTag } : { label: "<None>", tag: null },
                            onChange: updateTag
                        }}
                        isDisabled={readOnly}
                        options={checklistTagOptions}
                        isSearchable={false}
                        meta={step.errors && step.errors.checklistTag ? { touched: true, error: step.errors.checklistTag } : {}}
                    />
                </div>
                <div className='col-sm-6'>
                    <label>&nbsp;</label>
                    <CheckboxInput
                        input={{ checked: step.passiveOnly === true, onChange: togglePassiveOnly, disabled: readOnly }}
                        label="Passive Only"
                        className="inline"
                        meta={{}}
                    />
                </div>
            </div>
            {step.passiveOnly && <div className="form-group row">
                <div className='col-sm-6'>
                    <label>&nbsp;</label>
                    <CheckboxInput
                        input={{ checked: step.passiveContinuousReading === true, onChange: togglePassiveContinuousReading, disabled: readOnly }}
                        label="Enable continuous reading"
                        className="inline"
                        meta={{}}
                    />
                </div>
                <div className='col-sm-6'>
                    <label>&nbsp;</label>
                    <CheckboxInput
                        input={{ checked: step.passiveRequireUserSignoff === true, onChange: togglePassiveRequireUserSignoff, disabled: readOnly }}
                        label="Require user signoff"
                        className="inline"
                        meta={{}}
                    />
                </div>
            </div>}

            {/*
                // Temperature must be
                // UNDER => Under the low value, violation is anything over the low value
                // OVER => Over the high value, violation is anything under the high value
                // BETWEEN => Over the low value and under the high value, violation is anything under the low value or over the high value
            */}

            <div>
                <div className="form-group">
                    {!step.passiveOnly && <CheckboxInput
                        input={{ checked: step.recordHistory === true, onChange: toggleRecordHistory, disabled: readOnly }}
                        label="Retain Temperature Readings"
                        className="inline"
                        meta={{}}
                    />}
                                
                    <label>Temperature Constraint Type (optional)</label>
                    <SelectInput
                        input={{
                            value: step.constraintType ? constraintTypeOptions.find((option) => option.value === step.constraintType) : constraintTypeOptions[0],
                            onChange: updateConstraintType
                        }}
                        isDisabled={readOnly}
                        options={constraintTypeOptions}
                        isSearchable={false}
                        meta={step.errors && step.errors.constraintTypeOptions ? { touched: true, error: step.errors.constraintTypeOptions } : {}}
                        components={{ SingleValue : SelectIconSingleValue, Option : SelectIconOption }}
                    />

                    { step.constraintType == 'UNDER' &&
                        <div style={{paddingTop: 10}}>
                            <label>The temperature must be under</label>
                            <TextInput
                                input={{
                                    value: step.constraintLowValueDisplay ? step.constraintLowValueDisplay :
                                                (step.constraintLowValue ?
                                                     UserUnitService.convertFromSystemUnitToUserUnit(me.temperatureDisplayUnit, step.constraintLowValue) : ""),
                                    onChange: (event) => updateConstraintLowValue(event.target.value),
                                }}
                                type="text"
                                label={"Low Value"}
                                readOnly={readOnly}
                                meta={step.errors && step.errors.constraintLowValue ? { touched: true, error: step.errors.constraintLowValue } : {}}
                            />
                        </div>
                    }

                    { step.constraintType == 'OVER' &&
                        <div style={{paddingTop: 10}}>
                            <label>The temperature must be over</label>
                            <TextInput
                                input={{
                                    value: step.constraintHighValueDisplay ? step.constraintHighValueDisplay :
                                                (step.constraintHighValue ? 
                                                    UserUnitService.convertFromSystemUnitToUserUnit(me.temperatureDisplayUnit, step.constraintHighValue) : ""),
                                    onChange: (event) => updateConstraintHighValue(event.target.value),
                                }}
                                type="text"
                                label={"High Value"}
                                readOnly={readOnly}
                                meta={step.errors && step.errors.constraintHighValue ? { touched: true, error: step.errors.constraintHighValue } : {}}
                            />
                        </div>
                    }

                    { step.constraintType == 'BETWEEN' &&
                        <div style={{paddingTop: 10}}>
                            <label>The temperature must be between</label>
                            <TextInput
                                input={{
                                    value: step.constraintLowValueDisplay ? step.constraintLowValueDisplay :
                                        (step.constraintLowValue ? 
                                            UserUnitService.convertFromSystemUnitToUserUnit(me.temperatureDisplayUnit, step.constraintLowValue) : ""),
                                    onChange: (event) => updateConstraintLowValue(event.target.value),
                                }}
                                type="text"
                                label={"Low Value"}
                                readOnly={readOnly}
                                meta={step.errors && step.errors.constraintLowValue ? { touched: true, error: step.errors.constraintLowValue } : {}}
                            />
                            <TextInput
                                input={{
                                    value: step.constraintHighValueDisplay ? step.constraintHighValueDisplay :
                                                (step.constraintHighValue ? UserUnitService.convertFromSystemUnitToUserUnit(me.temperatureDisplayUnit, step.constraintHighValue) : ""),
                                    onChange: (event) => updateConstraintHighValue(event.target.value),
                                }}
                                type="text"
                                label={"High Value"}
                                readOnly={readOnly}
                                meta={step.errors && step.errors.constraintHighValue ? { touched: true, error: step.errors.constraintHighValue } : {}}
                            />
                        </div>
                    }

                    { readOnly && step.constraintCorrectiveAction && onCorrectiveAction ?
                    <div style={{paddingBottom: '16px'}}>
                        <div className="pull-right" style={{fontSize: '14px'}}>
                            <span><a onClick={() => setCorrectiveAction()}>{i18next.t('pages.checklists.view-corrective-action')}</a></span>
                        </div>
                    </div> 
                    :
                    <div>
                        { !readOnly && step.name && step.constraintType && (step.constraintLowValue || step.constraintHighValue) && onCorrectiveAction &&
                        <div style={{paddingBottom: '16px'}}>
                            <div className="pull-right" style={{fontSize: '14px'}}>
                                <span>
                                    <a onClick={() => setCorrectiveAction()}>{step.constraintCorrectiveAction ? "Edit Corrective Action" : i18next.t('pages.checklists.add-corrective-action')}</a>
                                </span>
                                { step.constraintCorrectiveAction && 
                                    <span> | <a onClick={removeConstraintCorrectiveAction}>{i18next.t('pages.checklists.remove-corrective-action')}</a></span>
                                }
                            </div>
                        </div>
                        }
                    </div>
                    }
                </div>
            </div>
            
        </div>
    );
};

export default connect(({user}) => ({me: user.me}) )(TemperatureStep);